"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { TableHead } from "@/components/ui/table"
import { GripVertical } from "lucide-react"

interface ResizableColumnHeaderProps {
  children: React.ReactNode
  columnKey: string
  defaultWidth?: number
  onResize?: (key: string, width: number) => void
}

export function ResizableColumnHeader({
  children,
  columnKey,
  defaultWidth = 200,
  onResize,
}: ResizableColumnHeaderProps) {
  const [width, setWidth] = useState(defaultWidth)
  const [isResizing, setIsResizing] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const diff = e.clientX - startXRef.current
      const newWidth = Math.max(80, startWidthRef.current + diff)
      setWidth(newWidth)

      if (onResize) {
        onResize(columnKey, newWidth)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isResizing, columnKey, onResize])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    startXRef.current = e.clientX
    startWidthRef.current = width
  }

  return (
    <TableHead
      style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
      className="relative group text-ellipsis overflow-hidden"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {children}
      <div
        className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize transition-all ${
          isResizing ? "bg-primary w-1.5" : isHovering ? "bg-border" : "bg-transparent"
        }`}
        onMouseDown={handleMouseDown}
      >
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-auto cursor-col-resize"
          onMouseDown={handleMouseDown}
        >
          <GripVertical
            className={`h-4 w-4 transition-opacity ${isResizing || isHovering ? "opacity-70" : "opacity-0"}`}
          />
        </div>
      </div>
    </TableHead>
  )
}

